// Librairies
import { addDays } from 'date-fns';
import i18n from '../locales/i18n';
// Utils
import DatesUtil from './DatesUtil';
import TreesUtil from './TreesUtil';
import { faCalendarClock, faCheck, faEdit, faPlus, faQuestion, faTimes, faTrash } from '@fortawesome/pro-solid-svg-icons';
import GreenSpacesUtil from './GreenSpacesUtil';

export default class ActionsUtil {
    static getRecurrencesInfos(actionGroup) {
        const startDate = DatesUtil.convertUTCDateToDate(actionGroup.startDate);
        const endDate = actionGroup.endDate ? DatesUtil.convertUTCDateToDate(actionGroup.endDate) : null;
        if (!actionGroup.recurrence) return i18n.t("Aucune");
        const recurrenceSplit = actionGroup.recurrence.split(' - ');
        const recurrenceValue = parseInt(recurrenceSplit[0]);
        const recurrenceUnit = recurrenceSplit[1];
        let recurrenceString; let nbRecurrences = 0;
        let tempDate = startDate;

        switch (recurrenceUnit) {
            case 'Jours':
                recurrenceString = i18n.t("Tous les jours", { count: recurrenceValue });
                // Calcul du nombre de récurrences totales dans le temps
                if (endDate)
                    while (tempDate <= endDate) {
                        nbRecurrences++;
                        tempDate.setDate(tempDate.getDate() + recurrenceValue);
                    }
                break;
            case 'Semaines':
                recurrenceString = i18n.t("Toutes les semaines", { count: recurrenceValue });
                // Calcul du nombre de récurrences totales dans le temps
                if (endDate)
                    while (tempDate <= endDate) {
                        nbRecurrences++;
                        tempDate.setDate(tempDate.getDate() + recurrenceValue * 7);
                    }
                break;
            case 'Mois':
                recurrenceString = i18n.t("Tous les mois", { count: recurrenceValue });
                // Calcul du nombre de récurrences totales dans le temps
                if (endDate)
                    while (tempDate <= endDate) {
                        nbRecurrences++;
                        tempDate = DatesUtil.addMonths(tempDate, recurrenceValue);
                    }
                break;
            case 'Années':
                recurrenceString = i18n.t("Tous les ans", { count: recurrenceValue });
                // Calcul du nombre de récurrences totales dans le temps
                if (endDate)
                    while (tempDate <= endDate) {
                        nbRecurrences++;
                        tempDate.setFullYear(tempDate.getFullYear() + recurrenceValue);
                    }
                break;
            default: break;
        }

        return recurrenceString + (nbRecurrences ? ' (' + nbRecurrences + 'x)' : '');
    }

    static getRecurrencePrice(recurrence, action, feature, priceList = null) {
        if (recurrence?.status === 'cancelled') return 0;
        if (recurrence?.customPrice) return Number(recurrence.customPrice);
        return ActionsUtil.getActionPrice(action, feature, priceList);
    }

    static getActionPrice(action, feature, priceList = null) {
        const customPrice = priceList?.prices?.find(price => price.actionId === action.id);
        const property = customPrice ? customPrice.property : action.defaultProperty;
        const thresholds = priceList?.priceThresholds?.find(threshold => threshold.property === property)?.values;

        if (!feature?.properties) return 0;
        switch (feature.properties.category) {
            case 'Arbre':
                const biggestTrunk = TreesUtil.getBiggestTrunkOf(feature.properties);
                if (!customPrice) {
                    if (!action.defaultProperty) return action.price1;
                    const height = biggestTrunk ? biggestTrunk.height / 100 : 0;
                    const isStumpOrChronoxyle = feature?.properties.isStump || feature?.properties.isChronoxyle;
                    const stumpDiameter = isStumpOrChronoxyle && TreesUtil.getStumpDiameterByCircumference(biggestTrunk ? biggestTrunk.circumference : 0);
                    if (isStumpOrChronoxyle ? stumpDiameter?.id === 1 : (height >= 0 && height <= 5)) return action.price1;
                    if (isStumpOrChronoxyle ? stumpDiameter?.id === 2 : (height > 5 && height <= 10)) return action.price2;
                    if (isStumpOrChronoxyle ? stumpDiameter?.id === 3 : (height > 10 && height <= 15)) return action.price3;
                    if (isStumpOrChronoxyle ? stumpDiameter?.id === 4 : (height > 15 && height <= 20)) return action.price4;
                    if (isStumpOrChronoxyle ? stumpDiameter?.id === 5 : (height > 20 && height <= 25)) return action.price5;
                    if (isStumpOrChronoxyle ? stumpDiameter?.id === 6 : height > 25) return action.price6;
                    return 0;
                }

                if (property && thresholds?.length) {
                    let value = biggestTrunk ? biggestTrunk[property] : 0;
                    if (['height', 'crownDiameter'].includes(property)) value /= 100;
                    for (let i = 0; i < thresholds.length; i++)
                        if (value <= thresholds[i]) return customPrice.values?.[i] || 0;
                    return customPrice.values?.at(-1) || 0;
                } else return customPrice.values?.[0] || 0;
            case 'Espace vert':
                if (feature.properties.dominantCompositionId === 7 && action.categories.includes('Arbre')) { // Massifs arborés
                    const woodedArea = GreenSpacesUtil.getMainWoodedArea(feature.properties.woodedAreas);
                    if (!woodedArea) return 0;

                    if (!customPrice) {
                        if (!action.defaultProperty) return action.price1 * woodedArea.nbTrees;
                        const height = woodedArea ? woodedArea.averageHeight / 100 : 0;
                        if (height >= 0 && height <= 5) return action.price1 * woodedArea.nbTrees;
                        if (height > 5 && height <= 10) return action.price2 * woodedArea.nbTrees;
                        if (height > 10 && height <= 15) return action.price3 * woodedArea.nbTrees;
                        if (height > 15 && height <= 20) return action.price4 * woodedArea.nbTrees;
                        if (height > 20 && height <= 25) return action.price5 * woodedArea.nbTrees;
                        if (height > 25) return action.price6 * woodedArea.nbTrees;
                        return 0;
                    }

                    if (property && thresholds?.length) {
                        const properties = { height: 'averageHeight', circumference: 'averageCircumference', crownDiameter: 'averageCrownDiameter' };
                        let value = woodedArea ? woodedArea[properties[property]] : 0;
                        if (['averageHeight', 'averageCrownDiameter'].includes(property)) value /= 100;
                        for (let i = 0; i < thresholds.length; i++)
                            if (value <= thresholds[i]) return (customPrice.values?.[i] || 0) * woodedArea.nbTrees;
                        return (customPrice.values?.at(-1) || 0) * woodedArea.nbTrees;
                    } else return (customPrice.values?.[0] || 0) * woodedArea.nbTrees;
                } else {
                    if (!customPrice) {
                        if (!action.defaultProperty) return action.price1;
                        return feature.properties.surface * action.price1;
                    }

                    const value = property === 'length'
                        ? feature.properties.baseLine?.length
                        : property === 'perimeter'
                            ? feature.properties.perimeter
                            : feature.properties.surface;
                    if (property && thresholds?.length) {
                        for (let i = 0; i < thresholds.length; i++)
                            if (value <= thresholds[i]) return (customPrice.values?.[i] || 0) * value;
                        return (customPrice.values?.at(-1) || 0) * value;
                    } else return (customPrice.values?.[0] || 0) * (property ? value : 1);
                }
            case 'Mobilier':
                if (!customPrice) return action.price1;
                else return customPrice.values?.[0] || 0;
            default: return 0;
        }
    }

    static getActionUrgency(actionsUrgency, occurrences) {
        if (actionsUrgency < 5 && occurrences.find(occurrence => !occurrence.status && DatesUtil.convertUTCDateToDate(occurrence.date) < DatesUtil.getToday()))
            return 5;
        if (actionsUrgency < 4 && occurrences.find(occurrence => !occurrence.status && DatesUtil.convertUTCDateToDate(occurrence.date) < addDays(DatesUtil.getToday(), 30)))
            return 4;
        if (actionsUrgency < 3 && occurrences.find(occurrence => !occurrence.status && DatesUtil.convertUTCDateToDate(occurrence.date) >= DatesUtil.getToday()))
            return 3;
        if (actionsUrgency < 2 && occurrences.find(occurrence => occurrence.status === 'done'))
            return 2;
        if (actionsUrgency < 1 && occurrences.find(occurrence => occurrence.status === 'cancelled'))
            return 1;
        return actionsUrgency;
    }

    static getElementActionsUrgency(elementId, occurrencesByElement, actionsById, category) {
        const occurrences = (occurrencesByElement.get(elementId) || []).filter(occurrence => actionsById.get(occurrence.actionId)?.categories.includes(category));
        return ActionsUtil.getActionUrgency(0, occurrences);
    }

    // Index construit une fois par lecteur : pas de recherche dans une boucle sur les éléments
    static getOccurrencesByElement(occurrences) {
        const occurrencesByElement = new Map();
        (occurrences || []).forEach(occurrence => {
            const elementOccurrences = occurrencesByElement.get(occurrence.elementId);
            if (elementOccurrences) elementOccurrences.push(occurrence);
            else occurrencesByElement.set(occurrence.elementId, [occurrence]);
        });
        return occurrencesByElement;
    }

    static getDayKey(date) {
        const localDate = DatesUtil.convertUTCDateToDate(date);
        return `${localDate.getFullYear()}-${String(localDate.getMonth() + 1).padStart(2, '0')}-${String(localDate.getDate()).padStart(2, '0')}`;
    }

    static getGroupKey(occurrence) {
        return occurrence.seriesId ? `s-${occurrence.seriesId}` : `a-${occurrence.actionId}-${ActionsUtil.getDayKey(occurrence.date)}`;
    }

    // Groupes calculés : une série, ou une action ponctuelle un jour donné
    static getActionGroups(occurrences, series, actions = null) {
        const seriesById = new Map((series || []).map(s => [s.id, s]));
        const actionsById = new Map((actions || []).map(action => [action.id, action]));
        const groups = new Map();
        const addGroup = (key, actionId, s, date) => groups.set(key, {
            key, actionId, action: actionsById.get(actionId), seriesId: s?.id || null, series: s || null, date: s ? s.startDate : date,
            startDate: s ? s.startDate : date, endDate: s ? s.endDate : date, recurrence: s ? s.recurrence : '1 - Années',
            isUrgent: s ? s.isUrgent : false, occurrences: []
        });
        (occurrences || []).forEach(occurrence => {
            const key = ActionsUtil.getGroupKey(occurrence);
            if (!groups.has(key)) addGroup(key, occurrence.actionId, occurrence.seriesId ? seriesById.get(occurrence.seriesId) : null, occurrence.date);
            groups.get(key).occurrences.push(occurrence);
        });
        groups.forEach(group => { if (ActionsUtil.areOccurrencesUrgent(group.occurrences)) group.isUrgent = true; });
        (series || []).forEach(s => { if (!groups.has(`s-${s.id}`)) addGroup(`s-${s.id}`, s.actionId, s, s.startDate); });
        return [...groups.values()].sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
    }

    // Urgence d'occurrences : celle des occurrences à faire, ou de l'une quelconque s'il n'y en a plus à faire
    static areOccurrencesUrgent(occurrences) {
        const hasToDo = occurrences.some(occurrence => !occurrence.status);
        return occurrences.some(occurrence => occurrence.isUrgent && (!hasToDo || !occurrence.status));
    }

    // Brouillon du formulaire de création recopié d'un groupe (« Dupliquer »), éventuellement limité à un élément
    static getGroupDraft(group, projectId, elementId = null) {
        const [recurrenceValue, recurrenceUnit] = group.recurrence.split(' - ');
        return {
            projectId, actionId: group.actionId, action: group.action, isUrgent: group.isUrgent, customFields: {},
            startDate: DatesUtil.convertUTCDateToDate(group.startDate), endDate: DatesUtil.convertUTCDateToDate(group.endDate),
            recurrenceValue, recurrenceUnit,
            elements: ActionsUtil.getGroupElements(group).filter(element => !elementId || element.elementId === elementId)
        };
    }

    // Éléments d'un groupe avec leur type : ceux de ses occurrences et, pour une série, ses membres
    static getGroupElements(group) {
        const elementTypes = new Map();
        group.occurrences.forEach(occurrence => elementTypes.set(occurrence.elementId, occurrence.elementType));
        (group.series?.elements || []).forEach(element => elementTypes.set(element.elementId, element.elementType));
        return [...elementTypes].map(([elementId, elementType]) => ({ elementId, elementType }));
    }

    static getGroupElementIds(group) {
        return [...new Set([...group.occurrences.map(occurrence => occurrence.elementId), ...(group.series?.elements || []).map(element => element.elementId)])];
    }

    static mergeOccurrences(occurrences, { created = [], updated = [], removedIds = [] } = {}) {
        const occurrencesById = new Map((occurrences || []).map(occurrence => [occurrence.id, occurrence]));
        [...created, ...updated].forEach(occurrence => occurrencesById.set(occurrence.id, occurrence));
        removedIds.forEach(id => occurrencesById.delete(id));
        return [...occurrencesById.values()];
    }

    static mergeSeries(series, { created = [], updated = [], removedIds = [] } = {}) {
        const seriesById = new Map((series || []).map(s => [s.id, s]));
        [...created, ...updated].forEach(s => seriesById.set(s.id, s));
        removedIds.forEach(id => seriesById.delete(id));
        return [...seriesById.values()];
    }

    static getActionHistoryIcon(type) {
        return {
            action_linked: faPlus,
            action_unlinked: faTrash,
            recurrence_validation: faCheck,
            recurrence_invalidation: faTimes,
            validation_date_update: faEdit,
            comment_addition: faPlus,
            comment_update: faEdit,
            planned_date_update: faCalendarClock,
            recurrence_deletion: faTrash
        }[type] || faQuestion;
    }
}